/**
 * Created by vladislav on 9/6/2022
 */

const Platform = function (pluginName, options) {
    BasePlugin.call(this, "platform", pluginName, options);

    this.paused = false;
    this._pageHidden = false;
    this._windowFocused = true;
    this._sdkPaused = false;

    this.callbacks = {};
    // seeded with the time so ids never repeat across a page reload: a host still holds the context of a call made
    // by the previous page and answers with its id, which must not match a call of this page
    this.callSeq = Date.now();
    this.nativeEventListeners = {};
};

Platform.prototype = Object.create(BasePlugin.prototype);
Platform.prototype.constructor = Platform;

Platform.prototype.initialize = function (callback) {
    this._initialize(callback);
};

Platform.prototype.subscribeToBrowserEvents = function () {
    this._pageVisibilitySubscribe();
    this._windowFocusSubscribe();
    this._safePaddingSubscribe();
    this._notifyActiveSubscribe();
};

Platform.prototype._pageVisibilitySubscribe = function () {
    if (document.hidden) {
        this._togglePageHide(true);
    }

    if (typeof document.hidden !== "undefined") {
        ["visibilitychange", "webkitvisibilitychange"].forEach((event) => {
            document.addEventListener(event, () => {
                this._togglePageHide(document.hidden);
            }, false);
        });
    }

    window.addEventListener("pagehide", () => {
        this._togglePageHide(true);
    }, false);

    window.addEventListener("pageshow", () => {
        this._togglePageHide(false);
    }, false);
};

// pageshow and visibilitychange do not always agree: a lazy tab restore or a bfcache pageshow can
// arrive when the tab is already in the background. The event claims the page is shown,
// document.hidden knows better - resuming on it unpauses the game into a hidden page, and nothing
// pauses it again because no further visibilitychange follows.
Platform.prototype._togglePageHide = function (hidden) {
    if (typeof document.hidden !== "undefined" && document.hidden) {
        hidden = true;
    }

    if (this._pageHidden === hidden) {
        return;
    }

    this._pageHidden = hidden;

    if (!hidden) {
        // A page can become visible without becoming focused - the screen unlocks, another window
        // moves off it - and the "windowBlur" that should have muted the music was never sent,
        // because the window was not the one that lost the focus. Resuming would then play music
        // into a window nobody is looking at; take the focus away first if the browser says so.
        this._syncWindowFocus();
    }

    if (hidden) {
        this.pause();
    } else if (!this._sdkPaused) {
        this.resume();
    }
};

Platform.prototype._toggleSdkPaused = function (sdkPaused) {
    if (this._sdkPaused === sdkPaused) {
        return;
    }

    this._sdkPaused = sdkPaused;

    if (sdkPaused) {
        this.pause();
    } else if (!this._pageHidden) {
        this.resume();
    }
};

Platform.prototype.isPageHidden = function () {
    return this._pageHidden;
};

Platform.prototype._initialize = function (callback) {
    callback();
};

// Switching to another application leaves the page visible - only the window loses focus - so
// document.hidden stays false and _pageVisibilitySubscribe never fires. Kept separate from
// pause/resume on purpose: listeners are expected to mute, not to pause the game loop.
// Desktop only, mobile browsers report an app switch through visibilitychange already.
Platform.prototype._windowFocusSubscribe = function () {
    if (connector.info.isMobile) {
        return;
    }

    window.addEventListener("focus", () => {
        this._toggleWindowFocus(true);
    }, false);

    window.addEventListener("blur", () => {
        this._toggleWindowFocus(false);
    }, false);

    // an ad or payment iframe can take the focus and never hand back a window "focus" event -
    // any input reaching the page means the window is focused again, whatever the frame did
    ["pointerdown", "keydown"].forEach((event) => {
        document.addEventListener(event, () => {
            this._toggleWindowFocus(true);
        }, true);
    });

    // _windowFocused starts out true, but a page opened in a background tab, or behind another
    // window, has never had the focus - and no "blur" will ever arrive to say so. Ask the browser.
    this._syncWindowFocus();
};

// Re-reads the real focus state. Only ever *takes* focus away: giving it needs a "focus" event or
// an input, which _windowFocusSubscribe listens for, so recovery is guaranteed the moment the
// user actually returns.
Platform.prototype._syncWindowFocus = function () {
    if (connector.info.isMobile || typeof document.hasFocus !== "function") {
        return;
    }

    if (!document.hasFocus()) {
        this._toggleWindowFocus(false);
    }
};

Platform.prototype._toggleWindowFocus = function (focused) {
    if (this._windowFocused === focused) {
        return;
    }
    this._windowFocused = focused;

    this.bus.trigger(focused ? "windowFocus" : "windowBlur");
};

Platform.prototype._notifyActiveSubscribe = function () {
    const notifyActive = () => {
        // console.log("notifyActive");
        connector.notifyActive();
    };

    document.addEventListener("pointerdown", notifyActive, true);
    document.addEventListener("keydown", notifyActive, true);
};

Platform.prototype._safePaddingSubscribe = function () {
    window.addEventListener("resize", () => {
        this._triggerSafePadding();
    }, false);
};

Platform.prototype.setSafePadding = function (safePadding) {
    this.safePadding = safePadding;
    this._triggerSafePadding();
};

Platform.prototype._triggerSafePadding = function () {
    const padding = JSON.stringify(this.getSafePadding());
    if (this._lastSafePadding === padding) {
        return;
    }
    this._lastSafePadding = padding;
    this.bus.trigger("updateSafePadding");
};

Platform.getGroupUrl = function () {
    switch (connector.info.source) {
        case "ok":
            if (connector.info.isMobile) {
                return `https://m.ok.ru/group/${connector.config.ok.groupId}`;
            }
            return `https://ok.ru/group/${connector.config.ok.groupId}`;
        case "vk":
            if (connector.info.isMobile) {
                return `https://m.vk.ru/${connector.config.vk.groupId}`;
            }
            return `https://vk.ru/${connector.config.vk.groupId}`;
        case "mm":
            if (connector.info.isMobile) {
                return `https://m.my.mail.ru/community/${connector.config.mm.groupId}`;
            }
            return `https://my.mail.ru/community/${connector.config.mm.groupId}`;
        case "mbga":
            return `http://yahoo-mbga.jp/group/${connector.config.mbga.groupId}`;
        case "sp_mbga":
            return `http://sp.mbga.jp/_grp_view?g=${connector.config.mbga.groupId}`;
        case "draugiem":
            return `https://www.draugiem.lv/${connector.config.draugiem.groupId}`;
        case "fotostrana":
            return `https://fotostrana.ru/public/${connector.config.fotostrana.groupId}`;
        case "discord":
            return connector.config.discord.groupUrl;
        default:
            return `https://www.facebook.com/${connector.config.instant.groupId}`;
    }
};

Platform.getGameUrl = function (source) {
    return connector.utils.guessPlatformURL({ source });
};

Platform.getRateUrl = function () {
    switch (connector.info.source) {
        case "test":
            return connector.utils.guessPlatformURL({ source: "android" });
        case "android":
            return `market://details?id=${connector.config.android.package}`;
        case "amazon":
            return `amzn://apps/android?p=${connector.config.amazon.package}`;
        case "macos":
        case "ios":
            return `itms-apps://itunes.apple.com/app/id${connector.config.ios.id}`;
    }
};

Platform.getExternalUrl = function (url) {
    if (url[0] === "/") {
        url = url.substr(1);
    }
    if (connector.platform.oneOf(connector.INSTANT, connector.TEST) || url.startsWith("http")) {
        return url;
    }

    return `${connector.config.deployment}/${url}`;
};

Platform.prototype.openUrl = function (url) {
    setTimeout(window.open.bind(window, url, "_blank"), 0);
};

Platform.prototype.hasVibration = function () {
    return typeof navigator !== "undefined" && typeof navigator.vibrate === "function" && connector.info.isMobile;
};

Platform.prototype.vibrate = function (options) {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate(options.pattern);
    }
};

Platform.prototype.isShareSupported = function () {
    return false;
};

// a platform with a native share sheet opens it over the game, so a checkbox that
// fires it on window close is a jarring surprise - those platforms turn it off
Platform.prototype.isShareCheckBoxAvailable = function () {
    return true;
};

Platform.prototype.shareNative = function (data, callback) {
    this.callNative("MessagesPlugin.inviteFriends", {
        title: data.title,
        message: data.text,
        link: data.url
    }, callback);
};

Platform.prototype.loadUserId = function () {
    this._userID = connector.dataLoader.load(SimpleDataLoader.TYPES.USER_ID);

    this.isNewUser = !this._userID;
    if (this.isNewUser) {
        this.setUserID(this.generateTmpId());
    }

    return this.isNewUser;
};

Platform.prototype.setUserID = function (id) {
    if (this._userID === id) {
        return;
    }

    console.log("Platform.setUserID", this._userID, id);

    const oldId = this._userID;

    this._userID = id;

    connector.dataLoader.save(SimpleDataLoader.TYPES.USER_ID, this._userID);

    this.bus.trigger("userIdChanged", {
        oldId,
        newId: id
    });
};

Platform.prototype.getUserID = function () {
    return this._userID;
};

Platform.prototype.generateTmpId = function () {
    return `__${Math.floor(Math.random() * 1000000000000000)}`;
};

Platform.prototype.canReview = function () {
    return false;
};

Platform.prototype.getLocalStoragePrefix = function () {
    return "";
};

Platform.prototype.hasAudioControls = function () {
    return false;
};

Platform.prototype.isFullscreenAvailable = function () {
    return this.oneOf(connector.DRAUGIEM) && !!this._getFullscreener();
};

Platform.prototype.isExitFullscreenAvailable = function () {
    return this.oneOf(connector.DRAUGIEM) && !!this._getFullscreener();
};

Platform.prototype.toggleFullScreen = function () {
    if (this._isFullScreen()) {
        this.exitFullscreen();
    } else {
        const fullscreener = this._getFullscreener();
        if (fullscreener) {
            fullscreener();
        }
    }
};

Platform.prototype.exitFullscreen = function (callback) {
    if (!this._isFullScreen()) {
        callback && callback();
        return;
    }

    if (document.exitFullscreen) {
        document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
    } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
    }

    if (callback) {
        setTimeout(callback, 100);
    }
};

Platform.prototype._getFullscreener = function () {
    const elem = document.documentElement;
    const fullscreener = elem.requestFullscreen || elem.webkitRequestFullscreen || elem.msRequestFullscreen || elem.mozRequestFullScreen;
    if (fullscreener) {
        return fullscreener.bind(elem);
    }
};

Platform.prototype._isFullScreen = function () {
    return document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement || document.mozFullScreenElement;
};

Platform.prototype.isAudioEnabled = function () {
    return undefined;
};

Platform.prototype.requestReview = function (callback) {
    if (this.isConnected()) {
        this._requestReview(callback);
    } else {
        callback(connector.CODE_FAILED);
    }
};

Platform.prototype._requestReview = function (callback) {
    callback(connector.CODE_FAILED);
};

Platform.prototype.isLatestVersionCheckSupported = function () {
    return false;
};

Platform.prototype.checkLatestVersion = function (callback) {
    callback(connector.CODE_FAILED);
};

Platform.prototype.canCreateShortcut = function () {
    if (!this.isConnected()) {
        return false;
    }

    return this._canCreateShortcut();
};

Platform.prototype._canCreateShortcut = function () {
    return false;
};

Platform.prototype.canCreateShortcutAsync = function (callback) {
    if (!this.isConnected()) {
        callback(false);
        return;
    }

    this._canCreateShortcutAsync(callback);
};

Platform.prototype._canCreateShortcutAsync = function (callback) {
    callback(this._canCreateShortcut());
};

Platform.prototype.createShortcut = function (callback) {
    if (callback) {
        callback(connector.CODE_FAILED);
    }
};

Platform.prototype.getShortcutMissionReward = function (callback) {
    if (!this.isConnected()) {
        callback(false);
        return;
    }

    this._getShortcutMissionReward(callback);
};

Platform.prototype._getShortcutMissionReward = function (callback) {
    callback(false);
};

Platform.prototype.canStartEntranceMission = function () {
    return this.isConnected() && this._canStartEntranceMission();
};

Platform.prototype._canStartEntranceMission = function () {
    return false;
};

Platform.prototype.startEntranceMission = function (callback) {
    if (callback) {
        callback(connector.CODE_FAILED);
    }
};

Platform.prototype.getEntranceMissionReward = function (callback) {
    if (!this.isConnected()) {
        callback(false);
        return;
    }

    this._getEntranceMissionReward(callback);
};

Platform.prototype._getEntranceMissionReward = function (callback) {
    callback(false);
};

Platform.prototype.getAccessToken = function () {

};

Platform.DecodeWebViewData = function (data) {
    if (data) {
        try {
            return decodeURIComponent(data);
        } catch (e) {
            return data;
        }
    }
    return data;
};

Platform.prototype.parsePayload = function (callback) {
    if (this.payloadCallbacks) {
        this.payloadCallbacks.push(callback);
        return;
    }

    this.payloadCallbacks = [callback];

    this._parsePayload((payload) => {
        const callbacks = this.payloadCallbacks;
        delete this.payloadCallbacks;

        payload = Platform.stripSecretKeys(payload);

        callbacks.forEach((callback) => callback(payload));
    });
};

// Platforms hand the whole launch data over as payload; social iframes (VK, OK, MyMailRu) put session
// credentials there (access_token, secret, auth_key, sid, session_key, sig...). Consumers log the payload
// as is, so the credentials are dropped once here for every platform.
Platform.SECRET_KEY_REGEXP = /token|secret|sig|key|session|(^|_)(sid|hash)(_|$)/i;

Platform.stripSecretKeys = function (payload) {
    if (!payload || typeof payload !== "object") {
        return payload;
    }

    const result = {};
    Object.keys(payload).forEach((key) => {
        if (!Platform.SECRET_KEY_REGEXP.test(key)) {
            result[key] = payload[key];
        }
    });
    return result;
};

Platform.prototype._parsePayload = function (callback) {
    if (connector.info.isNative) {
        callback();
        return;
    }

    callback(connector.utils.getRequestParameters());
};

Platform.prototype.parseOtherWays = function (callback) {
    callback();
};

Platform.prototype.nativeEvent = function (name, data) {
    if (this.oneOf(
        connector.RUSTORE,
        connector.ONESTORE,
        connector.AMAZON,
        connector.ANDROID,
        connector.GPG,
        connector.DIGITAL,
        connector.EPICGAMES,
        connector.IOS,
        connector.MACOS,
        connector.MICROSOFT,
        connector.G5WINDOWS,
        connector.MSIX
    )) {
        data = Platform.DecodeWebViewData(data);
        data = data && JSON.parse(data);
    }

    console.log(`Platform.nativeEvent ${name}: ${JSON.stringify(data)}`);

    const listener = name.split(".")[0];
    const event = name.split(".")[1];

    if (this.nativeEventListeners[listener]) {
        this.nativeEventListeners[listener](event, data);
    }
};

// every call gets its own id, so two calls of one method may be in flight: a slow store answers the first one late,
// and the second one, issued after the player gave up, is answered on its own
Platform.prototype.callNative = function (method, options, callback) {
    if (!callback && typeof options === "function") {
        callback = options;
        options = undefined;
    }

    const callId = ++this.callSeq;

    if (callback) {
        this.callbacks[callId] = { method, callback };
    }

    const params = {
        ...options || {},
        _callId: callId,
        _hasOptions: Boolean(options),
        _hasCallback: Boolean(callback)
    };

    this._callNative(method, params);
};

Platform.prototype._callNative = function () {

};

Platform.prototype.callCallback = function (method, code, options, callId) {
    if (this.oneOf(
        connector.RUSTORE,
        connector.ONESTORE,
        connector.AMAZON,
        connector.ANDROID,
        connector.GPG,
        connector.DIGITAL,
        connector.EPICGAMES,
        connector.IOS,
        connector.MACOS,
        connector.MICROSOFT,
        connector.G5WINDOWS,
        connector.MSIX
    )) {
        options = Platform.DecodeWebViewData(options);
        options = options ? JSON.parse(options) : {};
    }

    console.log(`Platform.callCallback ${method} - ${code} - ${JSON.stringify(options)}`);

    // a host that does not echo the id (older native builds, portal parents) keeps one context per method and answers
    // the newest call; either way the older calls of that method are stale once a newer one is answered
    callId = Number(callId) || Math.max(...this.listCalls(method));

    const entry = this.callbacks[callId];
    if (!entry || entry.method !== method) {
        // late, repeated or from before a page reload: nobody waits for it, so it is counted, not thrown
        connector.metrics.logEvent(connector.EVENTS.STATS.NATIVE_CALLBACK_DROPPED, { itemId: method });
        return;
    }

    this.listCalls(method).filter((id) => id <= callId).forEach((id) => {
        delete this.callbacks[id];
    });

    entry.callback(code, options);
};

Platform.prototype.listCalls = function (method) {
    return Object.keys(this.callbacks).map(Number).filter((id) => this.callbacks[id].method === method);
};

Platform.prototype.isTester = function () {
    const testers = connector.config.testers && connector.config.testers[connector.info.source];

    if (!testers) {
        return false;
    }

    const ids = testers.ids;
    const deviceIds = testers.deviceIds;

    const userId = this.getUserID();
    const deviceId = connector.info.deviceID;

    return userId && ids && ids.includes(userId)
        || deviceId && deviceIds && deviceIds.includes(deviceId);
};

Platform.prototype.locationReload = function () {
    connector.ads.closeStickyCallback(connector.utils.waitNoMore(1000, () => {
        window.location.reload();
    }));
};

Platform.prototype.isPaused = function () {
    return this.paused;
};

Platform.prototype.pause = function () {
    if (!this.paused) {
        this.paused = true;

        this.bus.trigger("pause");
    }
};

Platform.prototype.resume = function () {
    if (this.paused) {
        this.paused = false;

        this.bus.trigger("resume");
    }
};

// true only where switchGame delivers the payload and the target's _parsePayload returns it
Platform.prototype.canPassPayload = function () {
    return false;
};

// opaque fields a switchGame payload can carry end to end on a platform that supports it - add a new one here
// and every canPassPayload platform's switchGame/_parsePayload picks it up with no other changes.
// channel is deliberately not here - every platform resolves it its own way (campaign ids, attribution, etc).
Platform.CROSSPROMO_PAYLOAD_FIELDS = ["promo", "crossId", "invitedBy", "from", "payerTier"];

Platform.prototype.hasPublisherPlayerId = function () {
    return false;
};

// whether switchGame can take the player to another game of ours here - false until a platform enables it
Platform.prototype.canSwitchGame = function () {
    return false;
};

Platform.prototype.switchGame = function (url) {
    connector.platform.openUrl(url);
};

Platform.prototype.getSafePadding = function () {
    if (!this.safeAreaDiv) {
        this.safeAreaDiv = document.createElement("div");
        this.safeAreaDiv.style.position = "absolute";
        this.safeAreaDiv.style.top = "env(safe-area-inset-top)";
        this.safeAreaDiv.style.bottom = "env(safe-area-inset-bottom)";
        this.safeAreaDiv.style.left = "env(safe-area-inset-left)";
        this.safeAreaDiv.style.right = "env(safe-area-inset-right)";
        document.body.appendChild(this.safeAreaDiv);
    }

    const getStyle = function (prop) {
        if (typeof window.getComputedStyle !== "undefined") {
            return parseInt(window.getComputedStyle(this.safeAreaDiv).getPropertyValue(prop)) || 0;
        }
        if (typeof this.safeAreaDiv.currentStyle !== "undefined") {
            return parseInt(this.safeAreaDiv.currentStyle[prop]) || 0;
        }
        return 0;
    }.bind(this);

    return {
        top: getStyle("top"),
        bottom: getStyle("bottom"),
        left: getStyle("left"),
        right: getStyle("right")
    };
};

Platform.prototype.getFrameSize = function () {
    // innerWidth/innerHeight are whole css pixels; body is 100% of the viewport and reports its fractional size
    const rect = document.body.getBoundingClientRect();
    if (rect.width && rect.height) {
        return cc.size(rect.width, rect.height);
    }

    return cc.size(window.innerWidth, window.innerHeight);
};